// Simple storage wrapper that works without any native modules
// Pure JavaScript implementation with localStorage fallback for persistence

interface StorageData {
  [key: string]: string;
}

class SimpleStorage {
  private static data: StorageData = {};
  private static initialized = false;
  private static initPromise: Promise<void> | null = null;

  // Initialize storage with localStorage fallback
  private static async initialize(): Promise<void> {
    // If already initialized, return immediately
    if (this.initialized) return;
    
    // If initialization is in progress, wait for it
    if (this.initPromise) {
      await this.initPromise;
      return;
    }
    
    // Start initialization
    this.initPromise = (async () => {
      try {
        // Try to load data from localStorage if available (web environment)
        if (typeof window !== 'undefined' && window.localStorage) {
          const keys = Object.keys(localStorage);
          keys.forEach(key => {
            if (key.startsWith('bookapt_')) {
              const value = localStorage.getItem(key);
              if (value) {
                this.data[key] = value;
              }
            }
          });
          console.log('SimpleStorage initialized from localStorage with', Object.keys(this.data).length, 'items');
        } else {
          // For React Native, use memory-only storage
          console.log('SimpleStorage initialized with memory-only storage (React Native)');
        }
        
        this.initialized = true;
      } catch (error) {
        console.error('Error initializing SimpleStorage:', error);
        this.initialized = true; // Continue with empty storage
      } finally {
        this.initPromise = null;
      }
    })();
    
    await this.initPromise;
  }

  static async setItem(key: string, value: string): Promise<void> {
    try {
      await this.initialize();
      
      // Store in memory
      this.data[key] = value;
      
      // Try to persist to localStorage if available
      try {
        if (typeof window !== 'undefined' && window.localStorage) {
          localStorage.setItem(key, value);
          console.log(`Stored ${key} to localStorage`);
        } else {
          console.log(`Stored ${key} in memory only (React Native)`);
        }
      } catch (error) {
        console.log(`Stored ${key} in memory only (localStorage not available)`);
      }
    } catch (error) {
      console.error(`SimpleStorage: Error setting ${key}:`, error);
      // Don't throw - always succeed with memory storage
    }
  }

  static async getItem(key: string): Promise<string | null> {
    try {
      await this.initialize();
      
      const value = this.data[key] || null;
      if (value) {
        console.log(`Retrieved ${key}: found`);
      }
      return value;
    } catch (error) {
      console.error(`SimpleStorage: Error getting ${key}:`, error);
      return null;
    }
  }

  static async removeItem(key: string): Promise<void> {
    try {
      await this.initialize();
      
      // Remove from memory
      delete this.data[key];
      
      // Try to remove from localStorage if available
      try {
        if (typeof window !== 'undefined' && window.localStorage) {
          localStorage.removeItem(key);
          console.log(`Removed ${key} from localStorage`);
        } else {
          console.log(`Removed ${key} from memory only (React Native)`);
        }
      } catch (error) {
        console.log(`Removed ${key} from memory only (localStorage not available)`);
      }
    } catch (error) {
      console.error(`SimpleStorage: Error removing ${key}:`, error);
    }
  }

  static async multiRemove(keys: string[]): Promise<void> {
    try {
      await this.initialize();
      
      // Remove from memory
      keys.forEach(key => delete this.data[key]);
      
      // Try to remove from localStorage if available
      try {
        if (typeof window !== 'undefined' && window.localStorage) {
          keys.forEach(key => localStorage.removeItem(key));
          console.log(`Removed multiple keys from localStorage: ${keys.join(', ')}`);
        } else {
          console.log(`Removed multiple keys from memory only (React Native): ${keys.join(', ')}`);
        }
      } catch (error) {
        console.log(`Removed multiple keys from memory only (localStorage not available): ${keys.join(', ')}`);
      }
    } catch (error) {
      console.error(`SimpleStorage: Error removing multiple keys:`, error);
    }
  }

  static getItemSync(key: string): string | null {
    try {
      // Ensure initialized flag is set to prevent race conditions
      if (!this.initialized) {
        console.warn(`SimpleStorage: getItemSync called before initialization for key ${key}`);
        this.initialized = true; // Set to true to prevent further warnings
      }
      
      // Synchronous version for UI components - use memory data
      const value = this.data[key] || null;
      console.log(`Retrieved sync ${key}: ${value ? 'found' : 'null'}`);
      return value;
    } catch (error) {
      console.error(`SimpleStorage: Error getting sync ${key}:`, error);
      return null;
    }
  }

  // Check if storage is available
  static isAvailable(): boolean {
    try {
      return this.initialized || true; // Always return true, storage will initialize when needed
    } catch (error) {
      console.error('SimpleStorage: Error checking availability:', error);
      return false;
    }
  }

  // Force initialization immediately (call this at app startup)
  static async forceInitialize(): Promise<void> {
    console.log('🚀 SimpleStorage: Force initialization called');
    await this.initialize();
    console.log('✅ SimpleStorage: Initialized with', Object.keys(this.data).length, 'items');
  }
}

export default SimpleStorage;
